/**
 * One post and everything read about it (platform design §17.10). The
 * container reads the ids from the route and hands each reading to its own
 * section; the sections and their bodies render only what a route returned.
 */

import type { ReactNode } from 'react';

import { Page } from '../../../chrome/page';
import { useBreadcrumbLeaf } from '../../../chrome/shell';
import { EmptyState, QueryStates } from '../../../chrome/state';
import { usePost, usePostMetrics, type Window } from '../api/queries';
import type { PostReach, PostRow } from '../api/schemas';
import type { CardMenu } from '../shared/PostMenu';
import { figuresOf, isStory, readingOf } from '../publications/readings';
import { DailyStanceCard } from '../sentiment/DailyStanceCard';
import { splitCaption } from './format';
import { PostCard } from './PostCard';
import { SharedMediaSection } from './SharedMedia';
import { StoryCard, storyTitle } from './StoryCard';
import { pub } from './messages';
import { usePublicationWindow } from './search';
import {
  AudienceSection,
  DigestSection,
  MetricHistorySection,
  MetricsSection,
  TermsSection,
  type Ctx,
} from './Sections';

/** The post card with its latest metrics. A story reads none, so the read
 *  lives here rather than on the page. */
function PostHeader(props: {
  ctx: Ctx;
  post: PostRow;
  figures: PostReach | null;
  menu: CardMenu;
}): ReactNode {
  const { campaignId, tenantId, postId, window } = props.ctx;
  const metrics = usePostMetrics(campaignId, tenantId, postId, { window });
  return (
    <PostCard
      post={props.post}
      figures={props.figures}
      metrics={readingOf(metrics, figuresOf)}
      menu={props.menu}
    />
  );
}

export function Publication(props: {
  campaignId: string;
  tenantId: string;
  postId: string;
  /** Whether the reader may reclassify the post; the route answers it from
   *  the session, and anything that does not ask is read-only. */
  canReclassify?: boolean;
  /** The route's own window, so its post lookup and this page's share one cache key. */
  window?: Window;
}): ReactNode {
  const { campaignId, tenantId, postId } = props;
  const ownWindow = usePublicationWindow();
  const window = props.window ?? ownWindow;
  const post = usePost(campaignId, tenantId, postId, { window });
  const shown = post.data?.post;
  useBreadcrumbLeaf(
    shown === undefined
      ? undefined
      : shown === null
        ? pub('post.missing.title')
        : isStory(shown)
          ? storyTitle(shown)
          : (splitCaption(shown.caption).heading ?? pub('post.untitled')),
  );
  const ctx: Ctx = { campaignId, tenantId, postId, window };

  return (
    <Page>
      <QueryStates
        query={post}
        isEmpty={(data) => data.post === null}
        empty={
          <EmptyState title={pub('post.missing.title')} description={pub('post.missing.body')} />
        }
      >
        {(data) =>
          data.post === null ? null : isStory(data.post) ? (
            <StoryCard post={data.post} campaignId={campaignId} tenantId={tenantId} />
          ) : (
            <>
              <PostHeader
                ctx={ctx}
                post={data.post}
                figures={data.figures}
                menu={{ campaignId, tenantId, canReclassify: props.canReclassify ?? false }}
              />
              <DigestSection ctx={ctx} />
              <MetricsSection ctx={ctx} figures={data.figures} />
              <DailyStanceCard {...ctx} scopeRef={postId} />
              <MetricHistorySection ctx={ctx} />
              <TermsSection ctx={ctx} />
              <SharedMediaSection campaignId={campaignId} tenantId={tenantId} postId={postId} />
              <AudienceSection ctx={ctx} />
            </>
          )
        }
      </QueryStates>
    </Page>
  );
}
